import { phpAvatarPaths } from "../../core/php-data-access";
import { Roles } from "../../constants/roles";
import { translator } from "../../translation/translator";
import { CommonUtils } from "../../util/common-utils";
import type { PhpAuthUser } from "../../types/php-injected";
import type { UserModalFollower, UserModalFollowing } from "../modal/user-modal-api";
import { userFollowForm } from "./user-follow-form";

type FollowApiUser = UserModalFollower | UserModalFollowing;

interface UserFollowCardViewModel {
    user_id?: number;
    id?: number;
    display_name?: string;
    nick_name?: string;
    name?: string;
    full_name?: string;
    avatar?: string | null;
    posts_count?: number;
    review_count?: number;
    followers_count?: number;
    recommend_rank?: number | null;
    recommendRank?: number | null;
    date_of_birth?: string | null;
    gender?: string | null;
    skin_type?: string | null;
    skin_concerns?: string[] | string | null;
    role?: string | null;
    followings?: number[];
    followers?: number[];
}

export class UserFollowRender {
    static #followersEmptyHtml = `
        <p class="text-muted text-center" data-translate="no_followers" data-role="${Roles.USER_FOLLOW_FOLLOWERS_EMPTY}">
            ยังไม่มีผู้ติดตาม
        </p>
    `;

    static #skinTypeTranslateKey(value: string): string | null {
        const key = String(value || "").trim();
        if (!key) return null;
        const map: Record<string, string> = {
            normal: "skin_type_normal",
            oily: "skin_type_oily",
            dry: "skin_type_dry",
            combination: "skin_type_combination",
            delicate: "skin_type_delicate",
            sensitive: "skin_type_sensitive",
            dehydrated_oily: "skin_type_dehydrated_oily",
            not_sure: "skin_type_not_sure",
        };
        return map[key] || null;
    }

    static #skinConcernTranslateKey(value: string): string | null {
        const key = String(value || "").trim();
        if (!key) return null;
        const map: Record<string, string> = {
            acne: "skin_concern_acne",
            pores: "skin_concern_pores",
            blackheads: "skin_concern_blackheads",
            whiteheads: "skin_concern_whiteheads",
            dark_spots: "skin_concern_dark_spots",
            redness: "skin_concern_redness",
            wrinkles: "skin_concern_wrinkles",
            dullness: "skin_concern_dullness",
            oiliness: "skin_concern_oiliness",
            irritation: "skin_concern_irritation",
        };
        return map[key] || null;
    }

    static #parseId(value: number | null | undefined): number {
        const parsed = Number(value || 0);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    static #normalizeIdList(value: number[] | null | undefined): number[] {
        return Array.isArray(value)
            ? value.map((item) => Number(item || 0)).filter((item) => Number.isFinite(item) && item > 0)
            : [];
    }

    static #normalizeApiUser(user: FollowApiUser | null | undefined): UserFollowCardViewModel {
        return {
            user_id: UserFollowRender.#parseId(user?.user_id),
            id: 0,
            display_name: typeof user?.display_name === "string" ? user.display_name : undefined,
            nick_name: typeof user?.nick_name === "string" ? user.nick_name : undefined,
            name: typeof user?.name === "string" ? user.name : undefined,
            full_name: typeof user?.full_name === "string" ? user.full_name : undefined,
            avatar: typeof user?.avatar === "string" ? user.avatar : null,
            posts_count: typeof user?.posts_count === "number" ? user.posts_count : 0,
            review_count: typeof user?.review_count === "number" ? user.review_count : 0,
            followers_count: typeof user?.followers_count === "number" ? user.followers_count : 0,
            recommend_rank: typeof user?.recommend_rank === "number" ? user.recommend_rank : null,
            recommendRank: null,
            date_of_birth: typeof user?.date_of_birth === "string" ? user.date_of_birth : null,
            gender: typeof user?.gender === "string" ? user.gender : null,
            skin_type: typeof user?.skin_type === "string" ? user.skin_type : null,
            skin_concerns: Array.isArray(user?.skin_concerns)
                ? user.skin_concerns.map((value) => String(value ?? ""))
                : typeof user?.skin_concerns === "string"
                  ? user.skin_concerns
                  : null,
            role: typeof user?.role === "string" ? user.role : null,
            followings: [],
            followers: [],
        };
    }

    static #normalizeViewerUser(user: PhpAuthUser | null | undefined): UserFollowCardViewModel {
        return {
            user_id: UserFollowRender.#parseId(user?.user_id),
            id: UserFollowRender.#parseId(user?.id),
            display_name: typeof user?.display_name === "string" ? user.display_name : undefined,
            nick_name: typeof user?.nick_name === "string" ? user.nick_name : undefined,
            name: undefined,
            full_name: undefined,
            avatar: typeof user?.avatar === "string" ? user.avatar : null,
            posts_count: typeof user?.posts_count === "number" ? user.posts_count : 0,
            review_count: 0,
            followers_count: typeof user?.followers_count === "number" ? user.followers_count : 0,
            recommend_rank: null,
            recommendRank: null,
            date_of_birth: typeof user?.date_of_birth === "string" ? user.date_of_birth : null,
            gender: typeof user?.gender === "string" ? user.gender : null,
            skin_type: null,
            skin_concerns: null,
            role: typeof user?.role === "string" ? user.role : null,
            followings: UserFollowRender.#normalizeIdList(user?.followings),
            followers: UserFollowRender.#normalizeIdList(user?.followers),
        };
    }

    static #parseSkinConcerns(rawSkinConcerns: string[] | string | null | undefined): string[] {
        if (Array.isArray(rawSkinConcerns)) {
            return rawSkinConcerns.map((value) => String(value ?? "").trim()).filter((value) => value.length > 0);
        }

        if (typeof rawSkinConcerns !== "string") {
            return [];
        }

        const raw = rawSkinConcerns.trim();
        if (!raw) {
            return [];
        }

        try {
            const parsed = JSON.parse(raw) as unknown;
            if (Array.isArray(parsed)) {
                return parsed.map((value) => String(value ?? "").trim()).filter((value) => value.length > 0);
            }
        } catch {
            return raw
                .split(",")
                .map((value) => value.trim())
                .filter((value) => value.length > 0);
        }

        return [];
    }

    static #computeAgeRangeFromDob(dob: string | null | undefined): { low: number; high: number } | null {
        const raw = typeof dob === "string" ? dob.trim() : "";
        if (!raw) return null;

        const date = new Date(raw);
        if (Number.isNaN(date.getTime())) return null;

        const now = new Date();
        let age = now.getFullYear() - date.getFullYear();
        const monthDiff = now.getMonth() - date.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < date.getDate())) {
            age -= 1;
        }

        if (!Number.isFinite(age) || age < 0) return null;
        const low = Math.floor(age / 5) * 5;
        return { low, high: low + 4 };
    }

    static #renderRecommendTopBadge(userId: number, recommendRank: number | null): string {
        if (recommendRank === null) return "";

        return `
            <div class="user-card__top-badges">
                <span class="badge recommend user" data-role="${Roles.USER_RECOMMEND_BTN}" data-user-id="${userId}" data-is-recommended="1" data-recommend-rank="${recommendRank}" data-translate-title="user_recommend_tooltip" title="Recommended user" aria-label="V-Pick">
                    <i class="bi bi-bookmark-fill recommend-badge__icon" aria-hidden="true"></i>
                    <span class="recommend-badge__label">V-Pick</span>
                </span>
            </div>
        `.trim();
    }

    static #renderRecommendCompactBadge(userId: number, recommendRank: number | null, canRecommend: boolean): string {
        if (recommendRank === null && !canRecommend) return "";

        return `
            <span class="recommend-popup" data-role="${Roles.USER_RECOMMEND_BTN}" data-user-id="${userId}" data-can-recommend="${canRecommend ? "1" : "0"}" data-is-recommended="${recommendRank !== null ? "1" : "0"}" data-recommend-rank="${recommendRank ?? ""}" data-translate-title="user_recommend_tooltip" title="V-Pick" aria-label="V-Pick">
                <i class="bi bi-bookmark-fill recommend-badge__icon" aria-hidden="true"></i>
                <span class="recommend-badge__rank" aria-hidden="true">${recommendRank ?? ""}</span>
            </span>
        `.trim();
    }

    public static renderUserCardSlide(user: FollowApiUser, options: { followingsMode?: boolean } = {}): string {
        const normalizedUser = UserFollowRender.#normalizeApiUser(user);
        const userId = normalizedUser.user_id || normalizedUser.id || 0;
        if (userId <= 0) return "";

        const viewer = phpAvatarPaths ? ((window as Window & { authUser?: PhpAuthUser }).authUser ?? null) : null;
        const authUser = UserFollowRender.#normalizeViewerUser(viewer);
        const viewerUserId = authUser.user_id || authUser.id || 0;
        const viewerRole = typeof authUser.role === "string" ? authUser.role : "";
        const canRecommend = viewerRole === "admin" || viewerRole === "editor";
        const viewerFollowings = authUser.followings || [];
        const viewerFollowers = authUser.followers || [];
        const isFollowing = viewerFollowings.includes(userId);
        const targetFollowsViewer = viewerFollowers.includes(userId);
        const showFollowButton = viewerUserId > 0 && viewerUserId !== userId;
        const followButtonVariant = isFollowing
            ? "unfollow is-following"
            : targetFollowsViewer
              ? "followback"
              : "follow";
        const followTranslateKey = isFollowing ? "btn_unfollow" : targetFollowsViewer ? "btn_followback" : "btn_follow";
        const followButtonLabel = translator.translate(followTranslateKey);

        const displayName = String(
            normalizedUser.display_name ||
                normalizedUser.full_name ||
                normalizedUser.nick_name ||
                normalizedUser.name ||
                `User ${userId}`,
        );
        const safeName = CommonUtils.escapeHtml(displayName);
        const safeHref = CommonUtils.escapeHtml(CommonUtils.resolveUrl(`/users/${userId}`));
        const avatarUrl = UserFollowRender.buildAvatarUrl(normalizedUser.avatar || null);
        const safeAvatar = CommonUtils.escapeHtml(avatarUrl);
        const postsCount = normalizedUser.review_count || normalizedUser.posts_count || 0;
        const followersCount = normalizedUser.followers_count || 0;
        const recommendRaw = Number(normalizedUser.recommend_rank ?? normalizedUser.recommendRank ?? 0);
        const recommendRank = Number.isFinite(recommendRaw) && recommendRaw > 0 ? Math.trunc(recommendRaw) : null;

        const gender = typeof normalizedUser.gender === "string" ? normalizedUser.gender.trim() : "";
        const ageRange = UserFollowRender.#computeAgeRangeFromDob(normalizedUser.date_of_birth);
        const demographicsClass =
            gender === "male"
                ? "user-demographics-badge user-demographics-badge--male"
                : gender === "female"
                  ? "user-demographics-badge user-demographics-badge--female"
                  : "user-demographics-badge user-demographics-badge--neutral";
        const genderIcon =
            gender === "male" || gender === "female" || gender === "other" || gender === "prefer_not_to_say"
                ? "bi-person"
                : "";
        const genderTranslate = gender
            ? ` data-translate-title="gender_${CommonUtils.escapeHtmlAttribute(gender)}"`
            : "";
        const demographicsHtml = ageRange
            ? `<span class="badge badge-demographics badge--mini ${demographicsClass}"${genderTranslate}>${genderIcon ? `<i class="bi ${genderIcon}" aria-hidden="true"></i>` : ""}<span class="user-demographics-age" data-translate="age_range" data-low="${ageRange.low}" data-high="${ageRange.high}">${ageRange.low}-${ageRange.high}</span></span>`
            : `<span class="badge badge-demographics badge--mini ${demographicsClass}"${genderTranslate}>${genderIcon ? `<i class="bi ${genderIcon}" aria-hidden="true"></i>` : ""}<span class="user-demographics-age" data-translate="age_not_registered">ไม่ได้ระบุอายุ</span></span>`;

        const cardHtml = `
            <div class="user-item user-info card user-card" data-user-id="${userId}" data-can-recommend="${canRecommend ? "1" : "0"}"${
                showFollowButton
                    ? ` data-role="${Roles.USER_FOLLOW}" data-target-user-id="${userId}" data-initial-following="${isFollowing ? "1" : "0"}" data-target-follows-viewer="${targetFollowsViewer ? "1" : "0"}" data-follow-label-mode="reciprocal"`
                    : ""
            }>
                ${UserFollowRender.#renderRecommendTopBadge(userId, recommendRank)}
                <div class="user-card__row user-card__row--avatar">
                    <a class="user-card__avatar-link" href="${safeHref}">
                        ${safeAvatar ? `<img src="${safeAvatar}" alt="Avatar" class="user-card__avatar" loading="lazy">` : `<div class="user-card__avatar-placeholder user-card__avatar-placeholder--color-${Math.abs(Number(userId) || 0) % 10}" aria-hidden="true"><i class="bi bi-person-circle"></i></div>`}
                    </a>
                </div>
                <div class="user-card__row user-card__row--name">
                    ${UserFollowRender.#renderRecommendCompactBadge(userId, recommendRank, canRecommend)}
                    <a class="user-card__name" href="${safeHref}" data-role="${Roles.USER_FOLLOW_NAME}">${safeName}</a>
                </div>
                <div class="user-card__row user-card__row--demographics">
                    ${demographicsHtml}
                </div>
                <div class="user-card__row user-card__row--stats">
                    <div class="user-card__stats-group">
                        <span class="user-card__stat" data-translate-title="posts" data-role="${Roles.USER_FOLLOW_POSTS_COUNT}">
                            <i class="bi bi-file-earmark-text" aria-hidden="true"></i>
                            <span class="user-card__stat-value" data-role="${Roles.USER_FOLLOW_POSTS_VALUE}">${UserFollowRender.formatNumber(Number.isFinite(postsCount) ? postsCount : 0)}</span>
                            <span class="user-card__stat-label" data-translate="posts">posts</span>
                        </span>
                        <span class="user-card__stat" data-translate-title="followers" data-role="${Roles.USER_FOLLOW_FOLLOWERS_COUNT}">
                            <i class="bi bi-people" aria-hidden="true"></i>
                            <span class="user-card__stat-value" data-role="${Roles.USER_FOLLOW_FOLLOWERS_VALUE}">${UserFollowRender.formatNumber(Number.isFinite(followersCount) ? followersCount : 0)}</span>
                            <span class="user-card__stat-label" data-translate="followers">followers</span>
                        </span>
                    </div>
                </div>
                ${
                    showFollowButton
                        ? `<div class="user-card__row user-card__row--action"><button type="button" class="btn ${followButtonVariant}" data-role="${Roles.FOLLOW_TOGGLE_BTN}" aria-pressed="${isFollowing ? "true" : "false"}" title="${followButtonLabel}"><span class="user-follow-card-btn__label" data-role="${Roles.FOLLOW_TOGGLE_LABEL}" data-translate="${followTranslateKey}">${followButtonLabel}</span></button></div>`
                        : ""
                }
            </div>
        `.trim();

        const wrapperRole = options.followingsMode ? Roles.USER_PROFILE_FOLLOWING_CARD : Roles.USER_FOLLOW_ITEM;
        const wrapperAttrs = options.followingsMode
            ? ` data-following-user-id="${userId}"`
            : ' data-user-id="' + String(userId) + '"';
        return `
            <div class="home-slide user-card-slide${options.followingsMode ? " user-following-card" : ""}" data-role="${wrapperRole}" data-user-id="${userId}"${wrapperAttrs}>
                ${cardHtml}
            </div>
        `.trim();
    }

    public static parseCountText(text: string): number {
        const normalized = String(text || "")
            .replace(/[(),]/g, "")
            .trim();
        const parsed = Number.parseInt(normalized, 10);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    public static parseNumberText(text: string): number {
        return UserFollowRender.parseCountText(text);
    }

    public static formatNumber(value: number): string {
        return new Intl.NumberFormat().format(value);
    }

    public static formatCountText(value: number, wrapInParens = false): string {
        const formatted = UserFollowRender.formatNumber(Math.max(0, value));
        return wrapInParens ? `(${formatted})` : formatted;
    }

    public static buildAvatarUrl(avatar: string | null | undefined): string {
        const avatarConfig = phpAvatarPaths();
        const urlPath = typeof avatarConfig?.urlPath === "string" ? avatarConfig.urlPath.replace(/\/+$/, "") : null;
        const defaultFilename =
            typeof avatarConfig?.defaultFilename === "string" ? avatarConfig.defaultFilename.trim() : null;

        if (!avatar) return urlPath && defaultFilename ? `${urlPath}/${defaultFilename}` : "";
        if (avatar.startsWith("/") || avatar.startsWith("http")) return avatar;
        return urlPath ? `${urlPath}/${avatar}` : "";
    }

    public static updateFollowersCount(followersCountEl: HTMLElement | null, delta: number): void {
        if (!followersCountEl) return;
        const current = UserFollowRender.parseCountText(followersCountEl.textContent || "0");
        const next = Math.max(0, current + delta);
        followersCountEl.textContent = UserFollowRender.formatCountText(
            next,
            Boolean(followersCountEl.dataset.tabCount),
        );
    }

    public static removeFollowersEmptyMessage(followersListEl: HTMLElement): void {
        const empty = userFollowForm.getFollowersEmpty(followersListEl);
        if (empty) empty.remove();
    }

    public static ensureFollowersEmptyMessage(followersListEl: HTMLElement): void {
        const hasAnyUser = userFollowForm.getFollowerItems(followersListEl).length > 0;
        const empty = userFollowForm.getFollowersEmpty(followersListEl);
        if (hasAnyUser) {
            if (empty) empty.remove();
            return;
        }

        if (empty) return;
        followersListEl.insertAdjacentHTML("beforeend", UserFollowRender.#followersEmptyHtml.trim());
    }

    public static findFollowerUserItem(followersListEl: HTMLElement, followerUserId: number): HTMLElement | null {
        return (
            userFollowForm.getFollowerItem(followersListEl, followerUserId) ||
            userFollowForm.getFollowerItemByUserData(followersListEl, followerUserId)
        );
    }

    public static removeFollowerFromModalList(followersListEl: HTMLElement, followerUserId: number): void {
        const item = UserFollowRender.findFollowerUserItem(followersListEl, followerUserId);
        if (item) item.remove();
        UserFollowRender.ensureFollowersEmptyMessage(followersListEl);
    }

    public static addFollowerToModalList(followersListEl: HTMLElement, authUser: PhpAuthUser): void {
        const normalizedUser = UserFollowRender.#normalizeViewerUser(authUser);
        const followerUserId = normalizedUser.user_id || normalizedUser.id || 0;
        if (!Number.isFinite(followerUserId) || followerUserId <= 0) return;

        const already = UserFollowRender.findFollowerUserItem(followersListEl, followerUserId);
        if (already) {
            UserFollowRender.removeFollowersEmptyMessage(followersListEl);
            return;
        }

        UserFollowRender.removeFollowersEmptyMessage(followersListEl);
        const html = UserFollowRender.renderUserCardSlide(normalizedUser as FollowApiUser);
        if (!html) return;

        CommonUtils.insertHtml(followersListEl, html, "afterbegin");
        translator.updateContent();
    }
}
